import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Dropdown, type DropdownOption } from '../dropdown/dropdown';
import { FormattingService } from '../formatting/formatting.service';
import { Input as ZagInput } from '../input/input';
import { NumberInput } from '../number-input/number-input';
import { ZagIcon } from '../icon/icon';
import { ZagButton } from '../button/button';
import { DatePicker } from '../date-picker/date-picker';
import {
  RecurrenceIntervalInputComponent,
  type RecurrenceIntervalValue,
} from '../recurrence-interval-input/recurrence-interval-input.component';
import {
  describeRecurrenceRule,
  expandRecurrence,
  type PositionalWeekday,
  type RRuleDay,
  type RRuleFrequency,
  type RecurrenceRuleDescriptor,
  validateRecurrenceRule,
} from '@zag/calendar/models';
import {
  ALL_WEEKDAYS,
  DAYS_1_TO_31,
  MONTHS_1_TO_12,
  NTH_POSITIONS,
  deriveEndMode,
  deriveMonthlyRepeatMode,
  descriptorToRrule,
  emptyDescriptor,
  isEmptyDescriptor,
  type EndMode,
  type MonthlyRepeatMode,
  normalizeAfterFrequencyChange,
  parseIntList,
  safeParseDescriptor,
  serializeIntList,
  toggleNumber,
  toggleWeekday,
} from './rrule-editor.helpers';

const MAIN_FREQUENCIES: RRuleFrequency[] = ['daily', 'weekly', 'monthly', 'yearly'];

const SUB_DAILY_FREQUENCIES: RRuleFrequency[] = ['hourly', 'minutely', 'secondly'];

const DEFAULT_COUNT_MAX = 9999;
const DEFAULT_COUNT_SEED = 10;

const WEEKDAY_LABEL_KEYS: Record<RRuleDay, string> = {
  MO: 'calendars.rrule.weekdays.mo',
  TU: 'calendars.rrule.weekdays.tu',
  WE: 'calendars.rrule.weekdays.we',
  TH: 'calendars.rrule.weekdays.th',
  FR: 'calendars.rrule.weekdays.fr',
  SA: 'calendars.rrule.weekdays.sa',
  SU: 'calendars.rrule.weekdays.su',
};

const MONTH_LABEL_KEYS = [
  'calendars.rrule.months.1',
  'calendars.rrule.months.2',
  'calendars.rrule.months.3',
  'calendars.rrule.months.4',
  'calendars.rrule.months.5',
  'calendars.rrule.months.6',
  'calendars.rrule.months.7',
  'calendars.rrule.months.8',
  'calendars.rrule.months.9',
  'calendars.rrule.months.10',
  'calendars.rrule.months.11',
  'calendars.rrule.months.12',
];

type AdvancedNumericField = 'byHour' | 'byMinute' | 'bySecond' | 'byYearDay' | 'byWeekNo' | 'bySetPos';

@Component({
  selector: 'zag-rrule-editor',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    ZagIcon,
    ZagButton,
    Dropdown,
    NumberInput,
    ZagInput,
    DatePicker,
    RecurrenceIntervalInputComponent,
  ],
  templateUrl: './rrule-editor.component.html',
  styleUrl: './rrule-editor.component.scss',
  host: {
    class: 'zag-rrule-editor',
    '[class.zag-rrule-editor--disabled]': 'disabled()',
  },
})
export class RruleEditor {
  private readonly translate = inject(TranslateService);
  private readonly formatting = inject(FormattingService);

  /** Two-way bound RRULE string (without `RRULE:` or `DTSTART:` prefixes). */
  readonly value = model<string>('');

  readonly disabled = input<boolean>(false);

  /** Anchor date for the preview panel. Defaults to today when not provided. */
  readonly startDate = input<string | undefined>(undefined);

  /** Inclusive validity end date used to cap COUNT occurrences. */
  readonly endDate = input<string | undefined>(undefined);

  /** Show human-readable summary in the header. */
  readonly showSummary = input<boolean>(true);

  /** Show the preview panel (upcoming occurrences). */
  readonly showPreview = input<boolean>(true);

  /** Show the RRULE end-date option. */
  readonly showUntil = input<boolean>(true);

  /** Show the unbounded RRULE end option. */
  readonly showNever = input<boolean>(true);

  /** Show the "raw text" mode toggle. Hide it in forms that require a visual rule. */
  readonly allowTextMode = input<boolean>(true);

  readonly allowSubDailyFrequencies = input<boolean>(false);
  readonly allowAdvanced = input<boolean>(false);

  // ── Internal state ───────────────────────────────────────────────────────

  readonly mode = signal<'visual' | 'text'>('visual');
  readonly showAdvanced = signal<boolean>(false);
  readonly showSubDailyFrequencies = signal<boolean>(false);
  readonly monthlyMode = signal<MonthlyRepeatMode>('day');
  protected readonly intervalControl = new FormControl<RecurrenceIntervalValue>({ frequency: 'daily', interval: 1 }, { nonNullable: true });

  // endMode is tracked independently so that clearing COUNT/UNTIL does not snap the radio back to "Never".
  readonly endMode = signal<EndMode>('never');

  /** The descriptor currently being edited visually. Source of truth for visual mode. */
  readonly descriptor = signal<RecurrenceRuleDescriptor>(emptyDescriptor());

  /** Text draft held separately so the textarea can carry invalid in-progress edits. */
  readonly textDraft = signal<string>('');
  readonly textError = signal<string | null>(null);

  /** Advanced fields buffered as strings so the user can keep typing commas. */
  readonly advancedDraft = signal<Record<AdvancedNumericField, string>>({
    byHour: '',
    byMinute: '',
    bySecond: '',
    byYearDay: '',
    byWeekNo: '',
    bySetPos: '',
  });

  // ── Constants for template ───────────────────────────────────────────────

  protected readonly weekdays = ALL_WEEKDAYS;
  protected readonly weekdayLabelKeys = WEEKDAY_LABEL_KEYS;
  protected readonly daysOfMonth = DAYS_1_TO_31;
  protected readonly months = MONTHS_1_TO_12;
  protected readonly monthLabelKeys = MONTH_LABEL_KEYS;

  // ── Computed: dropdown option lists ──────────────────────────────────────

  /** Dropdowns expect pre-translated string labels. Re-translate on language change. */
  protected readonly wkstOptions = computed<DropdownOption<RRuleDay>[]>(() => {
    this.translateVersion(); // re-run when language changes
    return ALL_WEEKDAYS.map(day => ({ value: day, label: this.translate.instant(WEEKDAY_LABEL_KEYS[day]) }));
  });

  protected readonly weekdayDropdownOptions = computed<DropdownOption<RRuleDay>[]>(() => {
    this.translateVersion();
    return ALL_WEEKDAYS.map(day => ({ value: day, label: this.translate.instant(WEEKDAY_LABEL_KEYS[day]) }));
  });

  protected readonly nthPositionOptions = computed<DropdownOption<number>[]>(() => {
    this.translateVersion();
    return NTH_POSITIONS.map(p => ({ value: p.value, label: this.translate.instant(p.labelKey) }));
  });

  /** Signal incremented whenever the active language changes — invalidates translated options. */
  private readonly translateVersion = signal(0);

  // ── Computed: derived UI state ───────────────────────────────────────────

  readonly summary = computed(() => {
    const rule = this.rruleFromDescriptor();
    if (!rule) return '';
    return describeRecurrenceRule(rule);
  });

  protected readonly positionalDay = computed<RRuleDay>(() => this.descriptor().byDay?.[0]?.day ?? 'MO');
  protected readonly positionalN = computed<number>(() => this.descriptor().byDay?.[0]?.n ?? 1);
  protected readonly hasLastDayOfMonth = computed<boolean>(() => !!this.descriptor().byMonthDay?.includes(-1));
  protected readonly maxCount = computed(() => this.getMaxCount());
  protected readonly intervalFrequencies = computed<RRuleFrequency[]>(() =>
    this.showSubDailyFrequencies() && this.allowSubDailyFrequencies() ? [...MAIN_FREQUENCIES, ...SUB_DAILY_FREQUENCIES] : MAIN_FREQUENCIES
  );

  // ── Preview: upcoming occurrences ────────────────────────────────────────

  /** Raw ISO date keys (YYYY-MM-DD) for programmatic consumers. */
  readonly previewOccurrences = computed<string[]>(() => {
    const rule = this.value();
    if (!rule || !this.showPreview()) return [];
    const anchor = this.startDate() ?? isoToday();
    const end = this.endDate();
    try {
      const rangeEnd = end ?? addYearsIso(anchor, 2);
      const occ = expandRecurrence({ dateFrom: anchor, dateTo: end, recurrenceRule: rule }, anchor, rangeEnd);
      return occ.slice(0, 10);
    } catch {
      return [];
    }
  });

  /** Occurrences formatted with the active locale for human-readable display. */
  readonly previewOccurrencesFormatted = computed<Array<{ key: string; label: string }>>(() => {
    // Re-run whenever the formatting context (language/timezone) changes.
    const ctx = this.formatting.context();
    return this.previewOccurrences().map(key => ({
      key,
      label: this.formatting.formatDate(key, { locale: ctx.locale, timeZone: ctx.timeZone }),
    }));
  });

  // ── Sync effects ─────────────────────────────────────────────────────────

  private suppressNextValueEffect = false;

  constructor() {
    // Incoming value → hydrate visual descriptor & text draft
    effect(() => {
      const incoming = this.value();
      if (this.suppressNextValueEffect) {
        this.suppressNextValueEffect = false;
        return;
      }
      this.hydrateFromString(incoming);
    });

    // Skip emission when idle (empty value + default descriptor) so mount does not synthesize a default rule.
    effect(() => {
      if (this.mode() !== 'visual') return;
      const desc = this.descriptor();
      const val = this.value();
      if (!val && isEmptyDescriptor(desc)) return;
      const rule = this.safeDescriptorToRrule(desc);
      if (rule === null) return;
      if (rule !== val) {
        this.suppressNextValueEffect = true;
        this.value.set(rule);
      }
    });

    // Re-translate option labels when language changes
    this.translate.onLangChange.subscribe(() => this.translateVersion.update(v => v + 1));

    effect(() => {
      const maxCount = this.maxCount();
      if (this.endMode() !== 'count') return;
      this.descriptor.update(current => {
        if (current.count === undefined || current.count <= maxCount) return current;
        return { ...current, count: maxCount };
      });
    });

    effect(() => {
      const { frequency, interval } = this.descriptor();
      this.intervalControl.setValue({ frequency, interval: interval ?? 1 }, { emitEvent: false });
    });

    effect(() => {
      if (this.disabled()) this.intervalControl.disable({ emitEvent: false });
      else this.intervalControl.enable({ emitEvent: false });
    });

    this.intervalControl.valueChanges.pipe(takeUntilDestroyed()).subscribe(value => this.onIntervalControlChange(value));
  }

  // ── Template helpers ─────────────────────────────────────────────────────

  isWeekdaySelected(day: RRuleDay): boolean {
    return !!this.descriptor().byDay?.some(d => d.day === day && d.n === undefined);
  }

  isDayOfMonthSelected(day: number): boolean {
    return !!this.descriptor().byMonthDay?.includes(day);
  }

  isMonthSelected(month: number): boolean {
    return !!this.descriptor().byMonth?.includes(month);
  }

  // ── Public actions ───────────────────────────────────────────────────────

  setMode(mode: 'visual' | 'text'): void {
    if (this.mode() === mode) return;
    if (mode === 'visual') {
      this.hydrateFromString(this.textDraft());
    } else {
      const current = this.rruleFromDescriptor();
      this.textDraft.set(current);
      this.textError.set(null);
    }
    this.mode.set(mode);
  }

  toggleAdvanced(): void {
    this.showAdvanced.update(v => !v);
  }

  toggleSubDaily(): void {
    this.showSubDailyFrequencies.update(v => !v);
  }

  onFrequencyChange(freq: RRuleFrequency): void {
    this.descriptor.update(d => normalizeAfterFrequencyChange(d, freq));
    if (freq !== 'monthly' && freq !== 'yearly') this.monthlyMode.set('day');
  }

  onIntervalChange(interval: number | null): void {
    this.descriptor.update(d => {
      const next = { ...d };
      if (!interval || interval <= 1) delete next.interval;
      else next.interval = interval;
      return next;
    });
  }

  onWeekdayToggle(day: RRuleDay): void {
    this.descriptor.update(d => ({ ...d, byDay: toggleWeekday(d.byDay, day) }));
  }

  onDayOfMonthToggle(day: number): void {
    this.descriptor.update(d => ({ ...d, byMonthDay: toggleNumber(d.byMonthDay, day) }));
  }

  onLastDayOfMonthToggle(): void {
    this.descriptor.update(d => ({ ...d, byMonthDay: toggleNumber(d.byMonthDay, -1) }));
  }

  onMonthToggle(month: number): void {
    this.descriptor.update(d => ({ ...d, byMonth: toggleNumber(d.byMonth, month) }));
  }

  onMonthlyModeChange(mode: MonthlyRepeatMode): void {
    this.monthlyMode.set(mode);
    this.descriptor.update(d => {
      const next = { ...d };
      if (mode === 'day') {
        if (next.byDay?.some(b => b.n !== undefined)) {
          next.byDay = next.byDay.filter(b => b.n === undefined);
          if (next.byDay.length === 0) delete next.byDay;
        }
        delete next.bySetPos;
      } else {
        delete next.byMonthDay;
        if (!next.byDay?.some(b => b.n !== undefined)) {
          next.byDay = [{ day: 'MO', n: 1 }];
        }
      }
      return next;
    });
  }

  onPositionalDayChange(day: RRuleDay | null): void {
    if (!day) return;
    this.descriptor.update(d => {
      const n = d.byDay?.[0]?.n ?? 1;
      return { ...d, byDay: [{ day, n }] };
    });
  }

  onPositionalNChange(n: number | null): void {
    if (n === null) return;
    this.descriptor.update(d => {
      const day = d.byDay?.[0]?.day ?? 'MO';
      return { ...d, byDay: [{ day, n }] };
    });
  }

  onEndModeChange(mode: EndMode): void {
    this.endMode.set(mode);
    this.descriptor.update(d => {
      const next = { ...d };
      delete next.count;
      delete next.until;
      if (mode === 'count') next.count = Math.min(DEFAULT_COUNT_SEED, this.maxCount());
      if (mode === 'until') next.until = addMonthsIso(this.startDate() ?? isoToday(), 3);
      return next;
    });
  }

  onCountChange(count: number | null): void {
    // Only meaningful when the user is editing the COUNT radio option.
    if (this.endMode() !== 'count') return;
    this.descriptor.update(d => {
      const next = { ...d };
      if (!count || count <= 0) delete next.count;
      else next.count = Math.min(count, this.maxCount());
      return next;
    });
  }

  onUntilChange(until: string): void {
    if (this.endMode() !== 'until') return;
    this.descriptor.update(d => {
      const next = { ...d };
      if (!until) delete next.until;
      else next.until = until;
      return next;
    });
  }

  onWkstChange(wkst: RRuleDay | null): void {
    this.descriptor.update(d => {
      const next = { ...d };
      if (!wkst || wkst === 'MO') delete next.wkst;
      else next.wkst = wkst;
      return next;
    });
  }

  onAdvancedListChange(field: AdvancedNumericField, raw: string, config: { min: number; max: number; allowNegative?: boolean }): void {
    this.advancedDraft.update(d => ({ ...d, [field]: raw }));
    const parsed = parseIntList(raw, config);
    this.descriptor.update(d => {
      const next = { ...d };
      if (parsed.length === 0) delete next[field];
      else (next[field] as number[]) = parsed;
      return next;
    });
  }

  onTextInput(next: string): void {
    this.textDraft.set(next);
    const result = validateRecurrenceRule(next);
    if (result.valid) {
      this.textError.set(null);
      if (next !== this.value()) {
        this.suppressNextValueEffect = true;
        this.value.set(next);
        const parsed = safeParseDescriptor(next);
        this.descriptor.set(parsed.descriptor);
        this.endMode.set(deriveEndMode(parsed.descriptor));
      }
    } else {
      this.textError.set(result.error);
    }
  }

  copyRrule(): void {
    const text = this.value();
    if (!text) return;
    navigator.clipboard?.writeText(text).catch(() => {
      // Clipboard access is best-effort; browsers may block outside a user gesture.
    });
  }

  // ── Internal helpers ─────────────────────────────────────────────────────

  private onIntervalControlChange({ frequency, interval }: RecurrenceIntervalValue): void {
    const selected = this.intervalFrequencies().find(option => option === frequency);
    if (selected && selected !== this.descriptor().frequency) this.onFrequencyChange(selected);
    if (interval !== (this.descriptor().interval ?? 1)) this.onIntervalChange(interval);
  }

  private rruleFromDescriptor(): string {
    return this.safeDescriptorToRrule(this.descriptor()) ?? this.value();
  }

  private safeDescriptorToRrule(desc: RecurrenceRuleDescriptor): string | null {
    try {
      return descriptorToRrule(desc);
    } catch {
      return null;
    }
  }

  private hydrateFromString(rule: string): void {
    const parsed = safeParseDescriptor(rule);
    this.descriptor.set(parsed.descriptor);
    this.endMode.set(deriveEndMode(parsed.descriptor));
    this.monthlyMode.set(deriveMonthlyRepeatMode(parsed.descriptor));
    this.advancedDraft.set({
      byHour: serializeIntList(parsed.descriptor.byHour),
      byMinute: serializeIntList(parsed.descriptor.byMinute),
      bySecond: serializeIntList(parsed.descriptor.bySecond),
      byYearDay: serializeIntList(parsed.descriptor.byYearDay),
      byWeekNo: serializeIntList(parsed.descriptor.byWeekNo),
      bySetPos: serializeIntList(parsed.descriptor.bySetPos),
    });
    this.textDraft.set(rule);
    this.textError.set(parsed.error);

    if (
      parsed.descriptor.byHour?.length ||
      parsed.descriptor.byMinute?.length ||
      parsed.descriptor.bySecond?.length ||
      parsed.descriptor.byYearDay?.length ||
      parsed.descriptor.byWeekNo?.length ||
      parsed.descriptor.bySetPos?.length ||
      parsed.descriptor.wkst
    ) {
      if (this.allowAdvanced()) this.showAdvanced.set(true);
    }

    if (
      parsed.descriptor.frequency === 'hourly' ||
      parsed.descriptor.frequency === 'minutely' ||
      parsed.descriptor.frequency === 'secondly'
    ) {
      if (this.allowSubDailyFrequencies()) this.showSubDailyFrequencies.set(true);
    }
  }

  private getMaxCount(): number {
    const start = this.startDate();
    const end = this.endDate();
    if (!start || !end || start > end) return DEFAULT_COUNT_MAX;

    try {
      const recurrenceRule = descriptorToRrule({ ...this.descriptor(), count: undefined, until: undefined });
      const occurrences = expandRecurrence({ dateFrom: start, dateTo: end, recurrenceRule }, start, end);
      return Math.max(1, new Set(occurrences).size);
    } catch {
      return DEFAULT_COUNT_MAX;
    }
  }
}

function isoToday(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

function addMonthsIso(iso: string, months: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1 + months, d));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

function addYearsIso(iso: string, years: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y + years, m - 1, d));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

// Re-export types that consumers may want to reference directly
export type { PositionalWeekday };
